import { ECampaign, EUserFlow } from '@/context/borrower';
import { ITermsAndConditions } from '@/context/display';
import { RootState } from '@/context/store';
import { EWidgetLockScreen } from '@/partners/EWidgetLockScreen';
import { EMatchingEngineLenders } from '@/partners/matchingEngineEnums';
import {
    IMandatorySupportingDoc,
    IPartnerParams,
    ISignupOptions,
} from '@/partners/partnerTypes';
import { IVehicleAssetTypeButton } from '@/partners/partnerTypes';

export const selectShowWelcomeScreen = (state: RootState): boolean =>
    state.displayReducer.showWelcomeScreen;
export const selectIsWhiteLabel = (state: RootState): boolean =>
    state.displayReducer.isWhiteLabel;
export const selectLogo = (state: RootState): string =>
    state.displayReducer.logo;
export const selectFullLogoUrl = (state: RootState): boolean =>
    state.displayReducer.fullLogoUrl;
export const selectInitialLoad = (state: RootState): boolean =>
    state.displayReducer.initialLoad;
export const selectShowOcrDisplay = (state: RootState): boolean =>
    state.displayReducer.showOcrDisplay;
export const selectIsLoading = (state: RootState): boolean =>
    state.displayReducer.isLoading;
export const selectAgreedToTermsAndConditions = (
    state: RootState
): ITermsAndConditions => state.displayReducer.agreedToTermsAndConditions;
export const selectTermsAndConditionsLink = (state: RootState): string =>
    state.displayReducer.termsAndConditionsLink;
export const selectHideSellerScreen = (state: RootState): boolean =>
    state.displayReducer.hideSellerScreen;
export const selectTestDriveAgreement = (
    state: RootState
): string | undefined => state.displayReducer.partner.testDriveAgreement;
export const selectHideVehicleScreen = (state: RootState): boolean =>
    state.displayReducer.hideVehicleScreen;
export const selectShowTotalAmountPayable = (state: RootState): boolean =>
    state.displayReducer.showTotalAmountPayable;
export const selectPartnerName = (state: RootState): string | null =>
    state.displayReducer.partnerName;
export const selectPartnerEmail = (state: RootState): string | null =>
    state.displayReducer.partnerEmail;
export const selectPartnerNumber = (state: RootState): string | null =>
    state.displayReducer.partnerNumber;
export const selectPartnerOverwriteId = (state: RootState): string | null =>
    state.borrowerReducer.application.partnerOverwriteId;
export const selectSignupOptions = (
    state: RootState
): ISignupOptions | undefined => state.displayReducer.partner.signupOptions;
export const selectUseCarExpertCopy = (state: RootState): boolean =>
    state.displayReducer.partner.useCarExpertCopy === true;

/**
 * Whether this partner's widget is held closed until their setup fee is paid.
 *
 * `=== true`, not truthy: absent means unlocked, and an unset flag is omitted
 * from the partner payload rather than sent as false. A brand nobody has
 * touched must behave exactly as it did before the flag existed.
 */
export const selectWidgetLocked = (state: RootState): boolean =>
    state.displayReducer.partner.widgetLocked === true;

/**
 * Which screen a locked widget holds the applicant on.
 *
 * Resolves absent to HOLDING_SCREEN rather than returning undefined, so a
 * caller cannot accidentally treat "not configured" as a third state: a
 * brand locked before this field existed must keep showing what it showed
 * yesterday. Unlocked brands never reach here — read it with
 * `selectWidgetLocked`, not instead of it.
 *
 * An unrecognised value falls back the same way. The admin writes this field
 * and could add a screen to the enum before apply knows about it; a holding
 * screen is the safe thing to show when it does.
 */
export const selectWidgetLockScreen = (
    state: RootState
): EWidgetLockScreen => {
    const screen = state.displayReducer.partner.widgetLockScreen;
    return screen === EWidgetLockScreen.FROZEN_FIRST_SCREEN
        ? EWidgetLockScreen.FROZEN_FIRST_SCREEN
        : EWidgetLockScreen.HOLDING_SCREEN;
};
export const selectSkipExpenseScreen = (
    state: RootState
): boolean | undefined => state.displayReducer.partner.skipExpensesScreen;
export const selectWaterfallLenders = (
    state: RootState
): {
    lenders: EMatchingEngineLenders[];
    showLenderDetails: boolean;
}[] => state.displayReducer.waterfallLenders;
export const selectIsAssetKnownViaParams = (state: RootState): boolean =>
    state.displayReducer.isAssetKnownViaParams;
export const selectAskTermsAndConditions = (state: RootState): boolean | null =>
    state.displayReducer.askTermsAndConditions;
export const selectAvailableVehicleAssetTypes = (
    state: RootState
): IVehicleAssetTypeButton[] => state.displayReducer.availableVehicleAssetTypes;
export const selectUserFlow = (state: RootState): EUserFlow =>
    state.displayReducer.partner.userFlow;
export const selectPartner = (state: RootState): IPartnerParams =>
    state.displayReducer.partner;
export const selectMandatorySupportingDocuments = (
    state: RootState
): IMandatorySupportingDoc[] | undefined =>
    state.displayReducer.partner.mandatorySupportingDocuments;
export const selectPartnerCountry = (state: RootState): 'AU' | 'NZ' =>
    state.displayReducer.partner.country || 'AU';
export const selectIsDemoMode = (state: RootState): boolean =>
    state.displayReducer.isDemoMode;
export const selectCampaign = (state: RootState): ECampaign | undefined =>
    state.displayReducer.partner.campaign;

export const selectCountry = (state: RootState): 'AU' | 'NZ' =>
    (state.displayReducer.partner.country as 'AU' | 'NZ') ?? 'AU';
export const selectIsNZ = (state: RootState): boolean =>
    selectCountry(state) === 'NZ';
export const selectWidgetId = (state: RootState): string | null =>
    state.displayReducer.partner.widgetId ?? null;
